import { Wizard, Button, Modal, Dialog, DialogButtons } from '@st/theme'
import { ReactNode, useState } from 'react'

type Props = {
  years: number[]
  comingSoonYears?: number[]
  onClose: () => void
  onBack?: () => void
  children: (args: { taxYear: number; onBack: () => void }) => ReactNode
}
export function TaxYearSelector({ years, comingSoonYears = [], onClose, onBack, children }: Props) {
  const [selectedTaxYear, setSelectedTaxYear] = useState<number | undefined>(undefined)
  const [dialog, setDialog] = useState<{ type: 'open'; year: number } | undefined>(undefined)

  const latestSupportedYear = Math.max(...years.filter((year) => !comingSoonYears.includes(year)))

  if (selectedTaxYear == null) {
    return (
      <>
        <STSelectBinderTaxYearPage
          years={years}
          onClose={onClose}
          onBack={onBack}
          onTaxYearSelected={(year) => {
            if (comingSoonYears.includes(year)) {
              setDialog({ type: 'open', year })
            } else {
              setSelectedTaxYear(year)
            }
          }}
        />
        {dialog && (
          <Modal isOpen={true}>
            <Dialog
              title="Coming Soon"
              subtitle={`${dialog.year} organizers will be released on October 15th. For now feel free to try StanfordTax with ${latestSupportedYear} organizers!`}
              buttons={
                <DialogButtons>
                  <Button variant="default" onClick={() => setDialog(undefined)}>
                    Go Back
                  </Button>
                </DialogButtons>
              }
            />
          </Modal>
        )}
      </>
    )
  }

  return <>{children({ taxYear: selectedTaxYear, onBack: () => setSelectedTaxYear(undefined) })}</>
}

type STSelectBinderTaxYearPageProps = {
  years?: number[]
  latestN?: number
  maxYear?: number
  title?: string
  subtitle?: string
  onBack?: () => void
  onClose: () => void
  onTaxYearSelected: (taxYear: number) => void
}
function STSelectBinderTaxYearPage({
  years,
  latestN = 3,
  maxYear = new Date().getFullYear(),
  title = "Select the tax year of the binders you'd like to generate.",
  subtitle = 'For a given tax year, StanfordTax will use the data from the prior year tax software to generate binders and organizers.',
  onBack,
  onClose,
  onTaxYearSelected
}: STSelectBinderTaxYearPageProps) {
  const computedYears = years ?? Array.from({ length: latestN }, (_, i) => maxYear - i)
  return (
    <Wizard.Scaffold
      breadcrumb="Select tax year"
      onClose={onClose}
      bottomNav={
        onBack ? (
          <Wizard.BottomNav>
            <Button variant="default" onClick={onBack}>
              Back
            </Button>
          </Wizard.BottomNav>
        ) : undefined
      }
    >
      <Wizard.Main title={title} subtitle={subtitle}>
        <Wizard.OptionsColumn>
          {computedYears.map((year) => (
            <TaxYearOption key={year} taxYear={year} onTaxYearSelected={onTaxYearSelected} />
          ))}
        </Wizard.OptionsColumn>
      </Wizard.Main>
    </Wizard.Scaffold>
  )
}

function TaxYearOption({
  taxYear,
  onTaxYearSelected
}: {
  taxYear: number
  onTaxYearSelected: (taxYear: number) => void
}) {
  return (
    <Wizard.LargeOptionButton
      icon={() => <YearIcon year={taxYear} />}
      caption={`Generate binders and organizers for the ${taxYear} tax year using your ${
        taxYear - 1
      } tax software.`}
      onClick={() => onTaxYearSelected(taxYear)}
    >
      {taxYear} Tax Year Binders
    </Wizard.LargeOptionButton>
  )
}

function YearIcon({ year }: { year: number }) {
  return (
    <div className="mr-12 h-10 w-10 text-4xl font-bold text-blue-400">
      {"'" + String(year).slice(-2)}
    </div>
  )
}
